/**
 * INTERIM, hand-maintained list of the permissions the API declares.
 *
 * Source: `HrmPermissions` and `PermissionCatalog.All` in the API repo
 * (`api/src/Shared/TenelsHrm.Contracts/Permissions/`), described in
 * `api/docs/architecture/authorization.md`. The API publishes the same list at
 * `GET /api/v1/platform/permissions`.
 *
 * This file is replaced by `permission-catalog.generated.ts` once the generated client
 * lands and a step writes the catalog from the API. Until then, whoever adds a permission
 * to the API adds it here in the same change. `scripts/check-hrm-permission-literals.js`
 * reads this file while no generated catalog exists, and fails on any `Hrm.X.Y` literal
 * in `src/` that is not listed here.
 *
 * Last synced against the API at phase P007 (eight permissions). The rest are mock-page codes
 * taken from `plans/mock-pages-route-matrix.md`, kept coarse: one Read or Manage style gate per
 * area plus the special ones. The API names them per resource and may split them later. The
 * first eight keep their older plural spelling (`Hrm.Employees.View`, `Hrm.Roles.View`) because
 * existing routes and tests use it.
 */
export const PERMISSION_CATALOG = [
  { code: 'Hrm.Roles.View', sensitive: false, phase: 'P007' },
  { code: 'Hrm.Roles.Manage', sensitive: false, phase: 'P019' },
  { code: 'Hrm.Users.View', sensitive: false, phase: 'P019' },
  { code: 'Hrm.Users.Manage', sensitive: false, phase: 'P019' },
  { code: 'Hrm.Employees.View', sensitive: false, phase: 'P026' },
  { code: 'Hrm.Salary.ViewSensitive', sensitive: true, phase: 'P014' },
  { code: 'Hrm.Payroll.Approve', sensitive: true, phase: 'P088' },
  { code: 'Hrm.PlatformTenants.Create', sensitive: false, phase: 'P017' },
  // Platform and company (P011, P018, P019, P021, P036): company profile, users, roles, audit trail, imports. Each is the Read-style gate for its settings pages.
  { code: 'Hrm.Company.Read', sensitive: false, phase: 'P018' },
  { code: 'Hrm.Company.Update', sensitive: false, phase: 'P018' },
  { code: 'Hrm.User.Read', sensitive: false, phase: 'P019' },
  { code: 'Hrm.Role.Read', sensitive: false, phase: 'P019' },
  { code: 'Hrm.Audit.View', sensitive: false, phase: 'P011' },
  { code: 'Hrm.Import.View', sensitive: false, phase: 'P036' },
  // Operator console (P020). Create is the shell guard and View gates the list pages inside it.
  { code: 'Hrm.PlatformTenants.View', sensitive: false, phase: 'P020' },
  // Approvals (P032, P033): the inbox, and the chain editor.
  { code: 'Hrm.Approval.Read', sensitive: false, phase: 'P032' },
  { code: 'Hrm.ApprovalChain.Manage', sensitive: false, phase: 'P033' },
  // Organisation (P022-P025): locations, departments, designations, grades and employment types. Update is the single write gate for the area.
  { code: 'Hrm.Location.Read', sensitive: false, phase: 'P022' },
  { code: 'Hrm.Department.Read', sensitive: false, phase: 'P023' },
  { code: 'Hrm.Department.Update', sensitive: false, phase: 'P023' },
  // Employees (P026-P031, P037, P038). The two ViewSensitive codes unmask identity and payment accounts and are audited on read.
  { code: 'Hrm.Employee.Read', sensitive: false, phase: 'P026' },
  { code: 'Hrm.Employee.Create', sensitive: false, phase: 'P026' },
  { code: 'Hrm.Employee.Update', sensitive: false, phase: 'P026' },
  { code: 'Hrm.EmployeeIdentity.ViewSensitive', sensitive: true, phase: 'P027' },
  { code: 'Hrm.PaymentAccount.ViewSensitive', sensitive: true, phase: 'P027' },
  { code: 'Hrm.EmployeeDocument.Read', sensitive: false, phase: 'P030' },
  { code: 'Hrm.Invitation.View', sensitive: false, phase: 'P038' },
  // Time and attendance (P039-P056). Shift.* also gates calendars, rosters, devices and settings. AttendanceView.Read gates punches, dashboards and the grid. Regularization.View covers corrections, overtime and comp-off queues. Month lock is its own code.
  { code: 'Hrm.Shift.View', sensitive: false, phase: 'P041' },
  { code: 'Hrm.Shift.Manage', sensitive: false, phase: 'P041' },
  { code: 'Hrm.Clock.Use', sensitive: false, phase: 'P045' },
  { code: 'Hrm.AttendanceSummary.View', sensitive: false, phase: 'P049' },
  { code: 'Hrm.AttendanceSummary.ViewOwn', sensitive: false, phase: 'P049' },
  { code: 'Hrm.AttendancePolicy.Manage', sensitive: false, phase: 'P050' },
  { code: 'Hrm.Regularization.View', sensitive: false, phase: 'P051' },
  { code: 'Hrm.Regularization.Create', sensitive: false, phase: 'P051' },
  { code: 'Hrm.AttendanceView.Read', sensitive: false, phase: 'P055' },
  { code: 'Hrm.AttendanceMonth.Read', sensitive: false, phase: 'P056' },
  { code: 'Hrm.AttendanceMonth.Lock', sensitive: false, phase: 'P056' },
  // Leave (P057-P063). LeaveType.Manage is the write gate for policies and year-end. LeaveRequest.Read is team or all, by data scope.
  { code: 'Hrm.LeaveType.Manage', sensitive: false, phase: 'P057' },
  { code: 'Hrm.LeaveBalance.ReadAll', sensitive: false, phase: 'P059' },
  { code: 'Hrm.LeaveRequest.Read', sensitive: false, phase: 'P060' },
  { code: 'Hrm.LeaveRequest.Create', sensitive: false, phase: 'P060' },
  { code: 'Hrm.LeaveCalendar.Read', sensitive: false, phase: 'P062' },
  // Pay (P064-P084). Payroll.Prepare and Payroll.Approve are separate and no persona holds both. Compensation.Read gates increments, bonuses and commission input. Loan.Read gates loans, charges, deductions and claims. Payslip.ReadOwn is the employee own-payslip code.
  { code: 'Hrm.PayStructure.Read', sensitive: true, phase: 'P064' },
  { code: 'Hrm.PayStructure.Manage', sensitive: true, phase: 'P064' },
  { code: 'Hrm.Compensation.Read', sensitive: true, phase: 'P065' },
  { code: 'Hrm.Compensation.ViewSensitive', sensitive: true, phase: 'P065' },
  { code: 'Hrm.Payroll.Prepare', sensitive: true, phase: 'P075' },
  { code: 'Hrm.PayrollRun.Read', sensitive: true, phase: 'P075' },
  { code: 'Hrm.Payslip.ReadOwn', sensitive: true, phase: 'P079' },
  { code: 'Hrm.Payment.Generate', sensitive: true, phase: 'P080' },
  { code: 'Hrm.PayrollReport.Read', sensitive: true, phase: 'P083' },
  { code: 'Hrm.Loan.Read', sensitive: true, phase: 'P068' },
  // Incentives (P085-P088). Incentive.Read also gates commission runs and disputes.
  { code: 'Hrm.Incentive.Read', sensitive: false, phase: 'P085' },
  { code: 'Hrm.Incentive.Configure', sensitive: false, phase: 'P085' },
  { code: 'Hrm.Statement.ReadOwn', sensitive: false, phase: 'P087' },
  // People lifecycle (P089-P100). Case and Appraisal are restricted data.
  { code: 'Hrm.Requisition.Read', sensitive: false, phase: 'P089' },
  { code: 'Hrm.Candidate.Read', sensitive: false, phase: 'P090' },
  { code: 'Hrm.Checklist.Read', sensitive: false, phase: 'P093' },
  { code: 'Hrm.Probation.Read', sensitive: false, phase: 'P094' },
  { code: 'Hrm.Movement.Read', sensitive: false, phase: 'P095' },
  { code: 'Hrm.Letter.Read', sensitive: false, phase: 'P096' },
  { code: 'Hrm.Case.Read', sensitive: true, phase: 'P097' },
  { code: 'Hrm.Cycle.Read', sensitive: true, phase: 'P098' },
  { code: 'Hrm.Appraisal.Review', sensitive: true, phase: 'P098' },
  { code: 'Hrm.Separation.Read', sensitive: false, phase: 'P100' },
  // Experience (P101-P105). Ess.ReadOwn opens the self-service space. Manager.* opens the manager workspace and never shows pay.
  { code: 'Hrm.Ess.ReadOwn', sensitive: false, phase: 'P101' },
  { code: 'Hrm.Manager.Read', sensitive: false, phase: 'P102' },
  { code: 'Hrm.Manager.Approve', sensitive: false, phase: 'P102' },
  { code: 'Hrm.Announcements.Read', sensitive: false, phase: 'P103' },
  { code: 'Hrm.Announcements.Create', sensitive: false, phase: 'P103' },
  { code: 'Hrm.Helpdesk.Read', sensitive: false, phase: 'P104' },
  { code: 'Hrm.Assets.Read', sensitive: false, phase: 'P105' },
  // Reports (P106-P108).
  { code: 'Hrm.Reports.Read', sensitive: false, phase: 'P107' },
  { code: 'Hrm.Reports.PayrollCost', sensitive: true, phase: 'P107' },
  // --- Added after the mock pages landed. Spellings follow plans/mock-pages-route-matrix.md. The Update suffix on the org write codes follows Hrm.Department.Update, since the matrix says only "create, edit, delete".
  // Platform and company.
  { code: 'Hrm.Settings.Read', sensitive: false, phase: 'P018' },
  { code: 'Hrm.Settings.View', sensitive: false, phase: 'P015' },
  { code: 'Hrm.Settings.Update', sensitive: false, phase: 'P015' },
  { code: 'Hrm.Plan.Read', sensitive: false, phase: 'P021' },
  { code: 'Hrm.PlatformSecurity.View', sensitive: false, phase: 'P014' },
  { code: 'Hrm.PlatformSecurity.RotateKeys', sensitive: true, phase: 'P014' },
  { code: 'Hrm.Operator.TenantRead', sensitive: false, phase: 'P020' },
  { code: 'Hrm.Operator.TenantManage', sensitive: false, phase: 'P020' },
  { code: 'Hrm.Operator.AuditRead', sensitive: false, phase: 'P020' },
  { code: 'Hrm.Operator.FlagManage', sensitive: false, phase: 'P020' },
  { code: 'Hrm.Operator.PlanManage', sensitive: false, phase: 'P021' },
  { code: 'Hrm.Operator.Impersonate', sensitive: true, phase: 'P020' },
  { code: 'Hrm.Operator.AdminRecover', sensitive: true, phase: 'P020' },
  { code: 'Hrm.PlatformJobs.View', sensitive: false, phase: 'P012' },
  { code: 'Hrm.PlatformJobs.Trigger', sensitive: false, phase: 'P012' },
  // Organisation and employees.
  { code: 'Hrm.Location.Update', sensitive: false, phase: 'P022' },
  { code: 'Hrm.Designation.Read', sensitive: false, phase: 'P024' },
  { code: 'Hrm.Designation.Update', sensitive: false, phase: 'P024' },
  { code: 'Hrm.Grade.Read', sensitive: false, phase: 'P024' },
  { code: 'Hrm.Grade.Update', sensitive: false, phase: 'P024' },
  { code: 'Hrm.EmploymentType.Read', sensitive: false, phase: 'P025' },
  { code: 'Hrm.EmploymentType.Update', sensitive: false, phase: 'P025' },
  { code: 'Hrm.EmploymentStatus.Read', sensitive: false, phase: 'P025' },
  { code: 'Hrm.Employee.Export', sensitive: false, phase: 'P026' },
  { code: 'Hrm.Employee.CodeSettings', sensitive: false, phase: 'P026' },
  { code: 'Hrm.ReportingLine.Read', sensitive: false, phase: 'P028' },
  { code: 'Hrm.ReportingLine.Manage', sensitive: false, phase: 'P028' },
  { code: 'Hrm.EmployeeTimeline.Read', sensitive: false, phase: 'P031' },
  { code: 'Hrm.EmployeeDocument.Manage', sensitive: false, phase: 'P030' },
  { code: 'Hrm.EmployeeDocument.Report', sensitive: false, phase: 'P030' },
  { code: 'Hrm.Invitation.Create', sensitive: false, phase: 'P038' },
  { code: 'Hrm.Invitation.Revoke', sensitive: false, phase: 'P038' },
  { code: 'Hrm.Import.Create', sensitive: false, phase: 'P036' },
  { code: 'Hrm.Delegation.Manage', sensitive: false, phase: 'P034' },
  // Time and attendance.
  { code: 'Hrm.Holiday.View', sensitive: false, phase: 'P039' },
  { code: 'Hrm.Holiday.ViewOwn', sensitive: false, phase: 'P039' },
  { code: 'Hrm.Holiday.Manage', sensitive: false, phase: 'P039' },
  { code: 'Hrm.Holiday.Publish', sensitive: false, phase: 'P039' },
  { code: 'Hrm.WorkCalendar.View', sensitive: false, phase: 'P040' },
  { code: 'Hrm.WorkCalendar.ViewOwn', sensitive: false, phase: 'P040' },
  { code: 'Hrm.WorkCalendar.Manage', sensitive: false, phase: 'P040' },
  { code: 'Hrm.WorkCalendar.Assign', sensitive: false, phase: 'P040' },
  { code: 'Hrm.Roster.View', sensitive: false, phase: 'P042' },
  { code: 'Hrm.Roster.ViewOwn', sensitive: false, phase: 'P042' },
  { code: 'Hrm.Roster.Manage', sensitive: false, phase: 'P042' },
  { code: 'Hrm.Roster.EditPast', sensitive: false, phase: 'P042' },
  { code: 'Hrm.ShiftChange.View', sensitive: false, phase: 'P043' },
  { code: 'Hrm.ShiftChange.ViewOwn', sensitive: false, phase: 'P043' },
  { code: 'Hrm.ShiftChange.Create', sensitive: false, phase: 'P043' },
  { code: 'Hrm.ShiftChange.Respond', sensitive: false, phase: 'P043' },
  { code: 'Hrm.ShiftChange.ManageSettings', sensitive: false, phase: 'P043' },
  { code: 'Hrm.Punch.View', sensitive: false, phase: 'P046' },
  { code: 'Hrm.Punch.ViewLocation', sensitive: false, phase: 'P046' },
  { code: 'Hrm.Device.View', sensitive: false, phase: 'P047' },
  { code: 'Hrm.Device.Manage', sensitive: false, phase: 'P047' },
  { code: 'Hrm.Device.MapUsers', sensitive: false, phase: 'P047' },
  { code: 'Hrm.ManualEntry.View', sensitive: false, phase: 'P048' },
  { code: 'Hrm.ManualEntry.Create', sensitive: false, phase: 'P048' },
  { code: 'Hrm.ManualEntry.ApplyDirect', sensitive: false, phase: 'P048' },
  { code: 'Hrm.ManualEntry.Backdate', sensitive: false, phase: 'P048' },
  { code: 'Hrm.ManualEntry.ManageSettings', sensitive: false, phase: 'P048' },
  { code: 'Hrm.AttendancePolicy.View', sensitive: false, phase: 'P050' },
  { code: 'Hrm.AttendancePolicy.Activate', sensitive: false, phase: 'P050' },
  { code: 'Hrm.AttendancePolicy.Assign', sensitive: false, phase: 'P050' },
  { code: 'Hrm.Overtime.Read', sensitive: false, phase: 'P052' },
  { code: 'Hrm.Overtime.Request', sensitive: false, phase: 'P052' },
  { code: 'Hrm.Overtime.Enter', sensitive: false, phase: 'P052' },
  { code: 'Hrm.Overtime.Configure', sensitive: false, phase: 'P052' },
  { code: 'Hrm.Overtime.ReadLedger', sensitive: false, phase: 'P052' },
  { code: 'Hrm.CompOff.Read', sensitive: false, phase: 'P053' },
  { code: 'Hrm.CompOff.Request', sensitive: false, phase: 'P053' },
  { code: 'Hrm.CompOff.Manage', sensitive: false, phase: 'P053' },
  { code: 'Hrm.CompOff.Configure', sensitive: false, phase: 'P053' },
  { code: 'Hrm.AttendanceMonth.Import', sensitive: false, phase: 'P056' },
  { code: 'Hrm.AttendanceMonth.Feed', sensitive: false, phase: 'P056' },
  { code: 'Hrm.AttendanceMonth.Check', sensitive: false, phase: 'P056' },
  // Leave.
  { code: 'Hrm.LeaveYearEnd.Read', sensitive: false, phase: 'P063' },
  { code: 'Hrm.LeaveYearEnd.Prepare', sensitive: false, phase: 'P063' },
  { code: 'Hrm.LeaveYearEnd.Approve', sensitive: false, phase: 'P063' },
  { code: 'Hrm.LeaveYearEnd.Post', sensitive: false, phase: 'P063' },
  { code: 'Hrm.LeaveYearEnd.Undo', sensitive: false, phase: 'P063' },
  { code: 'Hrm.LeaveBalance.Read', sensitive: false, phase: 'P059' },
  { code: 'Hrm.LeaveBalance.Import', sensitive: false, phase: 'P059' },
  { code: 'Hrm.LeaveBalance.Adjust', sensitive: false, phase: 'P059' },
  { code: 'Hrm.LeaveAccrual.Read', sensitive: false, phase: 'P058' },
  { code: 'Hrm.LeaveAccrual.Run', sensitive: false, phase: 'P058' },
  { code: 'Hrm.LeaveAccrual.Adjust', sensitive: false, phase: 'P058' },
  { code: 'Hrm.LeavePolicy.Read', sensitive: false, phase: 'P057' },
  { code: 'Hrm.LeavePolicy.Manage', sensitive: false, phase: 'P057' },
  { code: 'Hrm.LeavePolicy.Override', sensitive: false, phase: 'P057' },
  // Pay. Every code here is sensitive: it opens or changes an amount.
  { code: 'Hrm.Increment.Read', sensitive: true, phase: 'P066' },
  { code: 'Hrm.Increment.Request', sensitive: true, phase: 'P066' },
  { code: 'Hrm.Increment.Bulk', sensitive: true, phase: 'P066' },
  { code: 'Hrm.Increment.Decrease', sensitive: true, phase: 'P066' },
  { code: 'Hrm.Bonus.Read', sensitive: true, phase: 'P067' },
  { code: 'Hrm.Bonus.Prepare', sensitive: true, phase: 'P067' },
  { code: 'Hrm.Bonus.ManageTypes', sensitive: true, phase: 'P067' },
  { code: 'Hrm.Commission.Read', sensitive: true, phase: 'P067' },
  { code: 'Hrm.Commission.Prepare', sensitive: true, phase: 'P067' },
  { code: 'Hrm.Commission.Approve', sensitive: true, phase: 'P067' },
  { code: 'Hrm.Deduction.Read', sensitive: true, phase: 'P069' },
  { code: 'Hrm.Deduction.Create', sensitive: true, phase: 'P069' },
  { code: 'Hrm.Deduction.Approve', sensitive: true, phase: 'P069' },
  { code: 'Hrm.Deduction.Configure', sensitive: true, phase: 'P069' },
  { code: 'Hrm.RecurringCharge.Read', sensitive: true, phase: 'P069' },
  { code: 'Hrm.OvertimeRule.Read', sensitive: true, phase: 'P070' },
  { code: 'Hrm.OvertimeRule.Manage', sensitive: true, phase: 'P070' },
  { code: 'Hrm.OvertimeRule.Confirm', sensitive: true, phase: 'P070' },
  { code: 'Hrm.Loan.Request', sensitive: true, phase: 'P068' },
  { code: 'Hrm.Loan.Disburse', sensitive: true, phase: 'P068' },
  { code: 'Hrm.Loan.Configure', sensitive: true, phase: 'P068' },
  { code: 'Hrm.Loan.Manage', sensitive: true, phase: 'P068' },
  { code: 'Hrm.Loan.Payoff', sensitive: true, phase: 'P068' },
  { code: 'Hrm.Loan.ApprovePayoff', sensitive: true, phase: 'P068' },
  { code: 'Hrm.Loan.WriteOff', sensitive: true, phase: 'P068' },
  { code: 'Hrm.PaySettings.Manage', sensitive: true, phase: 'P064' },
  { code: 'Hrm.PayrollRun.ConfirmPayment', sensitive: true, phase: 'P080' },
  { code: 'Hrm.PayrollRun.RequestReopen', sensitive: true, phase: 'P081' },
  { code: 'Hrm.PayrollRun.ApproveReopen', sensitive: true, phase: 'P081' },
  { code: 'Hrm.Payment.Read', sensitive: true, phase: 'P080' },
  { code: 'Hrm.Payment.Confirm', sensitive: true, phase: 'P080' },
  { code: 'Hrm.Payment.Download', sensitive: true, phase: 'P080' },
  { code: 'Hrm.Payment.Configure', sensitive: true, phase: 'P080' },
  { code: 'Hrm.PayrollRow.Read', sensitive: true, phase: 'P076' },
  { code: 'Hrm.PayrollRow.ReadAccount', sensitive: true, phase: 'P076' },
  { code: 'Hrm.PayrollSnapshot.Read', sensitive: true, phase: 'P077' },
  { code: 'Hrm.Payslip.Read', sensitive: true, phase: 'P079' },
  { code: 'Hrm.Payslip.Generate', sensitive: true, phase: 'P079' },
  { code: 'Hrm.Payslip.Publish', sensitive: true, phase: 'P079' },
  { code: 'Hrm.Settlement.Read', sensitive: true, phase: 'P082' },
  { code: 'Hrm.Settlement.Prepare', sensitive: true, phase: 'P082' },
  { code: 'Hrm.Settlement.Approve', sensitive: true, phase: 'P082' },
  { code: 'Hrm.Settlement.ConfirmPayment', sensitive: true, phase: 'P082' },
  { code: 'Hrm.Settlement.RequestReopen', sensitive: true, phase: 'P082' },
  { code: 'Hrm.Settlement.ApproveReopen', sensitive: true, phase: 'P082' },
  { code: 'Hrm.PayrollReport.Export', sensitive: true, phase: 'P083' },
  { code: 'Hrm.ParallelRun.Read', sensitive: true, phase: 'P084' },
  { code: 'Hrm.ParallelRun.Manage', sensitive: true, phase: 'P084' },
  { code: 'Hrm.ParallelRun.SignOffHr', sensitive: true, phase: 'P084' },
  { code: 'Hrm.PayrollImport.Read', sensitive: true, phase: 'P084' },
  { code: 'Hrm.PayrollImport.Run', sensitive: true, phase: 'P084' },
  // Incentives and the e-commerce link.
  { code: 'Hrm.Incentive.Targets', sensitive: false, phase: 'P086' },
  { code: 'Hrm.Incentive.Approve', sensitive: false, phase: 'P086' },
  { code: 'Hrm.CommissionRun.Read', sensitive: true, phase: 'P086' },
  { code: 'Hrm.CommissionRun.ReadDetail', sensitive: true, phase: 'P086' },
  { code: 'Hrm.CommissionRun.Create', sensitive: true, phase: 'P086' },
  { code: 'Hrm.CommissionRun.Calculate', sensitive: true, phase: 'P086' },
  { code: 'Hrm.CommissionRun.Approve', sensitive: true, phase: 'P086' },
  { code: 'Hrm.CommissionRun.Post', sensitive: true, phase: 'P086' },
  { code: 'Hrm.CommissionRun.Cancel', sensitive: true, phase: 'P086' },
  { code: 'Hrm.Statement.ReadAll', sensitive: true, phase: 'P087' },
  { code: 'Hrm.Statement.Publish', sensitive: true, phase: 'P087' },
  { code: 'Hrm.Dispute.ReadAll', sensitive: false, phase: 'P087' },
  { code: 'Hrm.Dispute.ReadOwn', sensitive: false, phase: 'P087' },
  { code: 'Hrm.Dispute.CreateOwn', sensitive: false, phase: 'P087' },
  { code: 'Hrm.Dispute.Resolve', sensitive: false, phase: 'P087' },
  { code: 'Hrm.EcomSync.Read', sensitive: false, phase: 'P085' },
  { code: 'Hrm.EcomSync.Run', sensitive: false, phase: 'P085' },
  { code: 'Hrm.EcomConnection.Read', sensitive: false, phase: 'P085' },
  { code: 'Hrm.EcomConnection.Manage', sensitive: false, phase: 'P085' },
  { code: 'Hrm.EcomAgentMap.Read', sensitive: false, phase: 'P085' },
  { code: 'Hrm.EcomAgentMap.Manage', sensitive: false, phase: 'P085' },
  // People lifecycle. Each sensitive-pay code guards one amount, so a viewer can hold one without the others.
  { code: 'Hrm.Requisition.ReadBudget', sensitive: true, phase: 'P089' },
  { code: 'Hrm.JobPosting.Read', sensitive: false, phase: 'P089' },
  { code: 'Hrm.CareerSite.Read', sensitive: false, phase: 'P090' },
  { code: 'Hrm.Offer.Read', sensitive: false, phase: 'P091' },
  { code: 'Hrm.Offer.ReadSalary', sensitive: true, phase: 'P091' },
  { code: 'Hrm.Letter.ReadSalary', sensitive: true, phase: 'P096' },
  { code: 'Hrm.Movement.ProposePay', sensitive: true, phase: 'P095' },
  { code: 'Hrm.Cycle.ProposePay', sensitive: true, phase: 'P098' },
  { code: 'Hrm.Course.Read', sensitive: false, phase: 'P099' },
  { code: 'Hrm.Certification.Read', sensitive: false, phase: 'P099' },
  { code: 'Hrm.Training.ReadCompliance', sensitive: false, phase: 'P099' },
  { code: 'Hrm.Training.ReadCost', sensitive: true, phase: 'P099' },
  { code: 'Hrm.Separation.ReadSensitive', sensitive: true, phase: 'P100' },
  // Experience.
  { code: 'Hrm.Manager.ApproveBulk', sensitive: false, phase: 'P102' },
  { code: 'Hrm.Manager.Delegate', sensitive: false, phase: 'P102' },
  { code: 'Hrm.Manager.ReadAttendance', sensitive: false, phase: 'P102' },
  { code: 'Hrm.Manager.ReadLeave', sensitive: false, phase: 'P102' },
  { code: 'Hrm.Announcements.ReadOwn', sensitive: false, phase: 'P103' },
  { code: 'Hrm.Announcements.Update', sensitive: false, phase: 'P103' },
  { code: 'Hrm.Announcements.Publish', sensitive: false, phase: 'P103' },
  { code: 'Hrm.Announcements.ReadTracking', sensitive: false, phase: 'P103' },
  { code: 'Hrm.Policies.Read', sensitive: false, phase: 'P103' },
  { code: 'Hrm.Policies.ReadOwn', sensitive: false, phase: 'P103' },
  { code: 'Hrm.Policies.Manage', sensitive: false, phase: 'P103' },
  { code: 'Hrm.Policies.Publish', sensitive: false, phase: 'P103' },
  { code: 'Hrm.Helpdesk.ReadOwn', sensitive: false, phase: 'P104' },
  { code: 'Hrm.Helpdesk.CreateOwn', sensitive: false, phase: 'P104' },
  { code: 'Hrm.Helpdesk.Respond', sensitive: false, phase: 'P104' },
  { code: 'Hrm.Helpdesk.Assign', sensitive: false, phase: 'P104' },
  { code: 'Hrm.Helpdesk.ReadConfidential', sensitive: true, phase: 'P104' },
  { code: 'Hrm.Assets.ReadOwn', sensitive: false, phase: 'P105' },
  { code: 'Hrm.Assets.Create', sensitive: false, phase: 'P105' },
  { code: 'Hrm.Assets.Update', sensitive: false, phase: 'P105' },
  { code: 'Hrm.Assets.Assign', sensitive: false, phase: 'P105' },
  { code: 'Hrm.Profile.ReadOwn', sensitive: false, phase: 'P101' },
  { code: 'Hrm.Profile.RequestChangeOwn', sensitive: false, phase: 'P101' },
  { code: 'Hrm.Profile.ReviewChange', sensitive: false, phase: 'P101' },
  { code: 'Hrm.Document.ReadOwn', sensitive: false, phase: 'P101' },
  // Reports.
  { code: 'Hrm.Reports.Export', sensitive: false, phase: 'P107' },
  { code: 'Hrm.ReportSchedules.Read', sensitive: false, phase: 'P108' },
  { code: 'Hrm.ReportSchedules.ReadAll', sensitive: false, phase: 'P108' },
  { code: 'Hrm.ReportSchedules.Create', sensitive: false, phase: 'P108' },
  { code: 'Hrm.ReportSchedules.Update', sensitive: false, phase: 'P108' },
  { code: 'Hrm.ReportSchedules.Delete', sensitive: false, phase: 'P108' },
] as const;

/** Every permission code, as a union of string literals. A typo in a guard fails to compile. */
export type Permission = (typeof PERMISSION_CATALOG)[number]['code'];

/** One permission, or several of which holding any one is enough. */
export type PermissionRequirement = Permission | readonly Permission[];

const KNOWN: ReadonlySet<string> = new Set(PERMISSION_CATALOG.map((p) => p.code));

/** True when the API declares this permission. */
export function isKnownPermission(code: string): code is Permission {
  return KNOWN.has(code);
}
